import { levelForXp } from '@runkr/config/economy'
import type { Levels } from '@runkr/config/economy'
import type { RewardMsg } from '@runkr/schema'
import { taskById } from '../daily/dailyView'
import type { ResultRow } from '../net/roomSnapshot'
import { Store } from '../net/store'
import { economyLevels, xpFill } from './profileView'

/** How long the results wait for the `"reward"` before saying "(not saved)" (PHASE2 T2.4). */
export const REWARD_WAIT_MS = 5000

/** `pending` until the reward or the timeout; `offline` = no Supabase, nothing is saved. */
export type SaveState = 'pending' | 'saved' | 'unsaved' | 'offline'

export interface RewardBar {
  /** Fill 0..1 before and after the race (from 0 after a level-up). */
  from: number
  to: number
  /** Level after the race. */
  level: number
}

/** One daily task this race moved (T2.5): "Use 8 items 5/8", "Task complete! +60 coins". */
export interface RewardTaskLine {
  id: string
  text: string
  progress: number
  target: number
  completedNow: boolean
  /** The task's coins (shown when `completedNow`). */
  coins: number
}

/** The last race's rewards as the results show them (`RewardLines`). */
export interface RewardView {
  xp: number
  coins: number
  save: SaveState
  /** Set once the reward arrived. */
  bar: RewardBar | null
  /** The new level when the race levelled the player up, else null. */
  levelUp: number | null
  /** Daily tasks this race moved, from the saved reward (empty until it arrives). */
  tasks: RewardTaskLine[]
}

/**
 * The reward's task lines, texts from `economy.json` (ids it does not know are skipped). A task
 * at its target that did not complete now was done before this race: it did not progress, so
 * it gets no line.
 */
export function rewardTaskLines(msg: RewardMsg): RewardTaskLine[] {
  const out: RewardTaskLine[] = []
  for (const t of msg.tasks ?? []) {
    const task = taskById(t.id)
    if (!task || (!t.completedNow && t.progress >= t.target)) continue
    out.push({
      id: t.id,
      text: task.text,
      progress: Math.min(t.progress, t.target),
      target: t.target,
      completedNow: t.completedNow,
      coins: task.coins,
    })
  }
  return out
}

export function rewardBar(msg: RewardMsg, levels: Levels = economyLevels()): RewardBar {
  const up = msg.level > msg.levelBefore
  return {
    from: up ? 0 : xpFill(msg.totalXp - msg.xp, levels),
    to: xpFill(msg.totalXp, levels),
    level: levelForXp(msg.totalXp, levels).level,
  }
}

/**
 * Results view model (T2.4): the own row's +XP / +coins from `ResultSchema`, then the saved
 * reward (bar, level-up) or "(not saved)" when no `"reward"` came within `REWARD_WAIT_MS` of the
 * results. The reward may arrive before the results patch; it is kept until `reset()`.
 */
export class RaceRewards {
  readonly view = new Store<RewardView | null>(null)
  private row: Pick<ResultRow, 'xp' | 'coinsEarned'> | null = null
  private msg: RewardMsg | null = null
  private timedOut = false
  private timer: ReturnType<typeof setTimeout> | null = null

  constructor(
    /** True when progress cannot be saved (offline guest mode). */
    private readonly offline: () => boolean,
    private readonly levels: Levels = economyLevels(),
  ) {}

  /** The results are up: `row` is the own result row (null if this player has none). */
  results(row: Pick<ResultRow, 'xp' | 'coinsEarned'> | null): void {
    if (!row || this.row) return
    this.row = { xp: row.xp, coinsEarned: row.coinsEarned }
    if (!this.msg && !this.offline()) {
      this.timer = setTimeout(() => {
        this.timer = null
        this.timedOut = true
        this.update()
      }, REWARD_WAIT_MS)
    }
    this.update()
  }

  reward(msg: RewardMsg): void {
    this.msg = msg
    this.clearTimer()
    this.update()
  }

  /** A new race (lobby, rematch): forget the last one. */
  reset(): void {
    this.clearTimer()
    this.row = null
    this.msg = null
    this.timedOut = false
    this.view.set(null)
  }

  private clearTimer(): void {
    if (this.timer) clearTimeout(this.timer)
    this.timer = null
  }

  private update(): void {
    const { row, msg } = this
    if (!row) return
    this.view.set({
      xp: msg?.xp ?? row.xp,
      coins: msg?.coins ?? row.coinsEarned,
      save: msg ? 'saved' : this.offline() ? 'offline' : this.timedOut ? 'unsaved' : 'pending',
      bar: msg ? rewardBar(msg, this.levels) : null,
      levelUp: msg && msg.level > msg.levelBefore ? msg.level : null,
      tasks: msg ? rewardTaskLines(msg) : [],
    })
  }
}
